import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/modules/auth'
import { TripsApi } from '@/shared/api/trips'
import type { SendTripInviteResult, TripFriendItem } from '@/shared/api/trips'

export const tripInviteFriendsQueryKey = (tripId: string) => ['trips', tripId, 'invite-friends'] as const

/** The user's friends who aren't on this trip yet, and sending them invites in one batch. */
export function useTripInviteFriends(tripId: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  const api = useMemo(() => new TripsApi(getAccessToken), [getAccessToken])
  const queryKey = tripInviteFriendsQueryKey(tripId)

  const friendsQuery = useQuery({
    queryKey,
    queryFn: () => api.getTripInviteFriends(tripId),
    staleTime: 60 * 1000,
  })

  const sendInvites = useMutation({
    mutationFn: (userIds: string[]) => api.sendTripInvites(tripId, userIds),
    onSuccess: (results: SendTripInviteResult[]) => {
      const nowPending = new Set(
        results.filter((r) => r.status === 'invited' || r.status === 'already_invited').map((r) => r.user_id)
      )
      queryClient.setQueryData<TripFriendItem[]>(queryKey, (friends) =>
        friends?.map((f) => (nowPending.has(f.id) ? { ...f, invite_status: 'pending' as const } : f))
      )
    },
  })

  return { friendsQuery, sendInvites }
}
